import type { WithAuthComponentProps } from '@/_hocs/with-auth'
import { WithAuth } from '@/_hocs/with-auth'
import {
  createFacilityLink,
  getApplicationColumns,
} from '@/features/_columns/application-table-columns'
import { DataTableFilter } from '@/features/_components/data-table-filter'
import { DataTablePagination } from '@/features/_components/data-table-pagination'
import { DateRangeFilter } from '@/features/_components/date-range-filter'
import { FiltersBar } from '@/features/_components/filters-bar'
import { InlineSearchbox } from '@/features/_components/inline-searchbox'
import { LoadingDataTable } from '@/features/_components/loading-data-table'
import { PersonsTable } from '@/features/_components/persons-table'
import { Container } from '@/features/_components/ui/container'
import { getStatusOptions } from '@/features/_constants/admission-status'
import { useCompanyContext } from '@/features/_contexts/company-context'
import { useFacilityContext } from '@/features/_contexts/facility-context'
import { parseStatusParam } from '@/features/_helpers/parse-status-param'
import { useSearchParams } from '@/features/_hooks/use-search-params'
import { useFacilityStore } from '@/features/_stores'
import { useSearch } from '@tanstack/react-router'
import { useEffect, useMemo } from 'react'
import { FacilityHeader } from '../../../_components/facility-header'
import { FacilityHeaderLoading } from '../../../_components/facility-header-loading'

const FacilityPersons = ({ session }: WithAuthComponentProps) => {
  const company = useCompanyContext()
  const facility = useFacilityContext()
  const userType = session.user.userType
  const isStaff = userType === 'staff'
  const searchParams = useSearch({ strict: false }) as {
    search?: string
    status?: string
    startDate?: string
    endDate?: string
  }

  const search = searchParams?.search ?? ''
  const { startDate, endDate } = searchParams
  const statusParam = searchParams?.status ?? ''
  const { setSearchParams } = useSearchParams()

  const status = useMemo(() => parseStatusParam(statusParam), [statusParam])
  const facilityLinkBuilder = useMemo(
    () => createFacilityLink(company?.slug ?? '', facility?.slug ?? ''),
    [company?.slug, facility?.slug],
  )

  const setCurrentFacility = useFacilityStore((s) => s.setCurrentFacility)

  useEffect(() => {
    if (facility) setCurrentFacility(facility)
  }, [facility, setCurrentFacility])

  if (!company || !facility) {
    const columns = getApplicationColumns({ showFacility: false, userType: session.user.userType })
    return (
      <Container className="px-14 py-10 gap-6">
        <FacilityHeaderLoading />
        <FiltersBar>
          <div className="flex items-center gap-2">
            <InlineSearchbox
              search=""
              placeholder="Busque por CPF, nome, telefone, email..."
              disabled
            />
            <DataTableFilter
              title="Status"
              filterKey="status"
              placeholder="Pesquise por status"
              options={getStatusOptions(userType)}
              disabled
            />
          </div>
          <DateRangeFilter disabled />
        </FiltersBar>
        <div className="flex min-h-0 flex-1 flex-col gap-2">
          <LoadingDataTable columns={columns} />
          <DataTablePagination
            currentPage={1}
            totalPages={1}
            totalCount={0}
            canGoNext={false}
            canGoPrevious={false}
            onNextPage={() => {}}
            onPreviousPage={() => {}}
          />
        </div>
      </Container>
    )
  }

  const hasAnyFilter = Boolean(search || statusParam || startDate || endDate)

  const handleClearAll = () => {
    setSearchParams({
      search: undefined,
      status: undefined,
      startDate: undefined,
      endDate: undefined,
    })
  }

  return (
    <Container className="px-14 py-10 gap-6">
      <FacilityHeader
        company={company}
        facility={facility}
        backHref={`/companies/${company.slug}/facilities`}
        breadcrumbs={
          isStaff
            ? [
                { label: 'Empresas', href: '/companies' },
                { label: company.name, href: `/companies/${company.slug}/facilities` },
                { label: facility.name },
              ]
            : [
                { label: 'Sua Instituição', href: `/companies/${company.slug}/facilities` },
                { label: facility.name },
              ]
        }
      />

      <FiltersBar>
        <div className="flex items-center gap-2">
          <InlineSearchbox search={search} placeholder="Busque por CPF, nome, telefone, email..." />
          <DataTableFilter
            title="Status"
            filterKey="status"
            placeholder="Pesquise por status"
            options={getStatusOptions(userType)}
          />
          {hasAnyFilter && (
            <button
              onClick={handleClearAll}
              className="ml-1 whitespace-nowrap text-xs text-muted-foreground hover:text-foreground"
            >
              Limpar tudo
            </button>
          )}
        </div>
        <DateRangeFilter />
      </FiltersBar>

      <PersonsTable
        mode="facility"
        facilityId={facility.id}
        companyId={company.id}
        userType={userType}
        status={status}
        startDate={startDate}
        endDate={endDate}
        search={search}
        linkBuilder={facilityLinkBuilder}
      />
    </Container>
  )
}

export default WithAuth(FacilityPersons, { allowedFor: ['staff', 'tenant'] })
